import React from 'react';
import {PhoneHQButton, PhoneHQGlyph, PhoneHQSwitch, PhoneHQSpinner, PhoneHQLoader, PhoneHQCombobox} from 'ch3-ui-lib';
import {Controller, useForm} from "react-hook-form";
import {getCallingCountries} from "../../../components/Dialer/services/countryDataService.ts";
import {FlagImage} from "react-international-phone";
import {useEffect, useMemo, useState} from "react";
import {selectorItem} from "../../../utilis/types/Types.ts";
import { useSearchParams } from 'react-router-dom';
import {
    ContactsImportConfig as OriginalContactsImportConfig,
    useGetPipedriveSettingsQuery, usePatchPipedriveIntegrationMutation, usePatchPipedrivePrefixMutation,
    useGetIntegrationImportsQuery, IntegrationStatus
} from "../../../store/features/integrations/integrationsService.ts";
import {showToast} from "../../../store/features/Notifications/notificationsSlice.tsx";
import {useAppDispatch} from "../../../store/store.ts";
import {DisconnectIntegrationDialog} from "../DisconnectIntegration.tsx";


interface OperationStatus {
    operationStatus: IntegrationStatus;
    finishDate?: string;
}

interface IntegrationStatusDisplayProps {
    integrationImport: OperationStatus;
    isIntegrationStarted: boolean;
}

const IntegrationStatusDisplay: React.FC<IntegrationStatusDisplayProps> = ({ integrationImport, isIntegrationStarted }) => {
    const formatFinishDate = (dateString?: string) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
            }).format(date);
        } catch (e) {
            console.error("Error formatting date:", e);
            return '';
        }
    };

    const formattedDate = formatFinishDate(integrationImport.finishDate);

    const getIntegrationStatus = (integration: OperationStatus, isStarted: boolean) => {
        if (isStarted) {
            return IntegrationStatus.RUNNING;
        }
        return integration.operationStatus;
    }
    switch (getIntegrationStatus(integrationImport, isIntegrationStarted)) {
        case IntegrationStatus.RUNNING:
            return (
                <div>
                    <PhoneHQSpinner label={'Import is in progress...'} />
                    <div className='text-ink-muted text-sm mt-2'>
                        This might take a moment. You can continue using PhoneHQ in the meantime.
                    </div>
                </div>
            );
        case IntegrationStatus.FINISHED:
            return (
                <div className='flex items-center gap-2 text-sm text-ink-muted'>
                    <span>Last sync</span>
                    <div className='h-6 w-6 bg-success-tint flex items-center rounded-full justify-center'>
                        <span className="inline-flex size-3 shrink-0 items-center justify-center rounded-full bg-success-tint text-success-ink"><PhoneHQGlyph name='check' size={8} className='text-white bg-success'/></span>
                    </div>
                    <span className='text-success-ink'>Success</span>
                    {formattedDate && <span>{formattedDate}</span>}
                </div>
            );
        case IntegrationStatus.FAILED:
            return (
                <div className='flex items-center gap-2 text-sm text-ink-muted'>
                    <span>Last sync</span>
                    <div className='h-6 w-6 bg-danger-tint flex items-center rounded-full justify-center'>
                        <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-danger-tint text-danger-ink"><PhoneHQGlyph name='info' size={14} className='text-white bg-danger'/></span>
                    </div>
                    <span className='text-danger-ink'>Failed</span>
                    {formattedDate && <span>{formattedDate}</span>}
                </div>
            );
        default:
            return null;
    }
};

export interface ContactsImportConfig extends OriginalContactsImportConfig {
    configuration: OriginalContactsImportConfig['configuration'] & {
        defaultNumbersPrefix: string | null;
    };
}

export const PipedriveSettings = () => {

    const dispatch = useAppDispatch();

    const [phrase, setPhrase] = useState('');
    const [isIntegrationStarted, setIntegrationStarted] = useState(false);
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');

    const {data: settings, isLoading} = useGetPipedriveSettingsQuery({integrationId: Number(id)});
    const  [patchPipedriveIntegration] = usePatchPipedriveIntegrationMutation();

    const [patchPipedrivePrefix] = usePatchPipedrivePrefixMutation();

    const {data: integrationImportData} = useGetIntegrationImportsQuery(
        {integrationType: 'PIPEDRIVE', sortBy: 'ID', order: 'DESC', limit: 1},
        {
            pollingInterval: isIntegrationStarted ? 5000 : undefined,
        }
    );
    const integrationImport = integrationImportData?.data?.[0];

    const {
        control,
        handleSubmit,
        setValue,
        getValues
    } = useForm<ContactsImportConfig>({
        mode: 'onBlur',
        defaultValues: {
            id: Number(id),
            type: "CONTACTS_IMPORT",
            platform: "PIPEDRIVE",
            details: {
                type: "",
                companyName: "",
                companyId: ""
            },
            configuration: {
                type: "",
                sendRecordings: false,
                sendCallSummary: false,
                sendMissedCalls: true,
                defaultNumbersPrefix: '',
                sendCallTranscription: false,
                matchWithDeals: false
            }
        },
    });

    useEffect(() => {
        if (settings) {
            setValue('configuration.sendRecordings', settings.configuration.sendRecordings);
            setValue('configuration.sendCallSummary', settings.configuration.sendCallSummary);
            setValue('configuration.type', settings.platform.toLowerCase());
            setValue('configuration.sendMissedCalls', settings.configuration.sendMissedCalls);
            setValue('configuration.defaultNumbersPrefix', settings.configuration.defaultNumbersPrefix || null);
            setValue('configuration.sendCallTranscription', settings.configuration.sendCallTranscription);
            setValue('configuration.matchWithDeals', settings.configuration.matchWithDeals);
            setValue('details.type',  settings.platform.toLowerCase());
            setValue('type', settings.type);
        }
    }, [settings, setValue]);

    useEffect(() => {
        if (!integrationImport) return;
        if ( integrationImport.operationStatus === IntegrationStatus.FINISHED || integrationImport.operationStatus === IntegrationStatus.FAILED) {
            setIntegrationStarted(false);
        }
    }, [integrationImport]);

    const prefixItems: selectorItem[] = useMemo(() => {
        const filteredCountries = getCallingCountries()
            .filter(
                (callingCountry) =>
                    callingCountry.name &&
                    (callingCountry.name.toLowerCase().includes(phrase.toLowerCase()) ||
                        callingCountry.countryCallingCodes[0].includes(phrase))
            )
            .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0));

        const allPrefixItems: selectorItem[] = [
            { label: 'None', value: null, key: '', prefixElement: null },
            ...filteredCountries.map((country) => {
                const callingCode = country.countryCallingCodes?.[0] ?? '';

                return {
                    label: `${country.name} (${callingCode})`,
                    value: country.alpha2,
                    key: country.alpha2,
                    prefixElement: (
                        <FlagImage
                            size={24}
                            iso2={country.alpha2.toLowerCase()}
                            className={'ml-3'}
                        />
                    ),
                };
            })
        ];

        return allPrefixItems.filter((item) =>
            item.label && item.label.toLowerCase().includes(phrase.toLowerCase())
        );
    }, [phrase]);

    const handlePhraseChange = (phrase: string) => {
        setPhrase(phrase);
    };

    const  onSubmit = async (data: ContactsImportConfig) => {
        try {
            await patchPipedriveIntegration({integrationId: Number(id), body: data}).unwrap();
            dispatch(showToast({
                title: "Success",
                description: "Integration settings saved successfully.",
                type: "success",
            }));
        } catch {
            dispatch(showToast({
                title: "Error",
                description: "Failed to save settings.",
                type: "error",
            }));
        }

    }

    const [disconnectOpen, setDisconnectOpen] = useState(false);

    if (isLoading) {
        return (
            <div className={`flex w-full`}>
                <PhoneHQLoader/>
            </div>
        );
    }
    const startPipedriveSync = () => {
        const defaultPrefix = getValues('configuration.defaultNumbersPrefix');
        patchPipedrivePrefix({id: Number(id), body: {defaultNumbersPrefix: defaultPrefix}})
        setIntegrationStarted(true)
    };
    return (
        <div className=' max-h-[calc(100vh-110px-var(--header-height))] overflow-y-auto'>
            <div className={'mx-auto max-w-[840px] my-6 mb-8 flex flex-col gap-6 border-line border-b pb-8'}>
                <div className='my-2 '>
                    <div className={'font-semibold mb-5 text-lg pl-3'}>
                        Settings
                    </div>
                    <Controller
                        name="configuration.sendRecordings"
                        control={control}
                        render={({field}) => (
                            <PhoneHQSwitch checked={field.value}
                                    onCheckedChange={() => field.onChange(!field.value)}
                                    description={'Save audio recordings of calls in Pipedrive.'}
                                    label={'Send recordings to Pipedrive'}/>
                        )}
                    />

                </div>
                <div className='my-2'>
                    <Controller
                        name="configuration.sendCallTranscription"
                        control={control}
                        render={({field}) => (
                            <PhoneHQSwitch checked={field.value}
                                    onCheckedChange={() => field.onChange(!field.value)}
                                    description={'Automatically save the full conversation text in Pipedrive.'}
                                    label={'Send transcript to Pipedrive'}/>
                        )}
                    />

                </div>
                <div className='my-2'>
                    <Controller
                        name="configuration.sendCallSummary"
                        control={control}
                        render={({field}) => (
                            <PhoneHQSwitch checked={field.value}
                                    onCheckedChange={() => field.onChange(!field.value)}
                                    description={'Automatically save a short summary of the conversation in Pipedrive.'}
                                    label={'Send summary to Pipedrive'}/>
                        )}
                    />

                </div>
                <div className='bg-brand-tint-subtle border border-brand flex p-4 gap-1 text-sm text-ink ml-3'>
                    <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand-ink"><PhoneHQGlyph name='info' size={24} className='pb-8 pl-0'/></span>
                    To send recordings, transcripts, and call summaries to Pipedrive, make sure call recording is active
                    in the settings of the selected phone number.
                </div>
                <div className='my-2'>
                    <Controller
                        name="configuration.sendMissedCalls"
                        control={control}
                        render={({field}) => (
                            <PhoneHQSwitch checked={field.value}
                                    onCheckedChange={() => field.onChange(!field.value)}
                                    label={'Add missed calls to Pipedrive'}/>
                        )}
                    />

                </div>
                <div className='my-2'>
                    <Controller
                        name="configuration.matchWithDeals"
                        control={control}
                        render={({field}) => (
                            <PhoneHQSwitch checked={field.value}
                                    onCheckedChange={() => field.onChange(!field.value)}
                                    label={'Automatic matching of calls with deals'}/>
                        )}
                    />

                </div>
                <div className='pl-3 flex justify-between items-center'>
                    <div className='font-semibold mb-3'>
                        Default country prefix for imported person's phone numbers
                    </div>
                    <Controller
                        name="configuration.defaultNumbersPrefix"
                        control={control}
                        render={({field}) => (
                            <div className={' mb-3 flex items-end gap-3'}>
                                <div className={' h-[66px]'}>
                                    <PhoneHQCombobox
                                        label="Prefix"
                                        placeholder="Search"
                                        onInputChange={handlePhraseChange}
                                        value={field.value ?? settings?.configuration.defaultNumbersPrefix}
                                        onSelect={(item) => field.onChange(item.value)}
                                        items={prefixItems}/>
                                </div>

                            </div>
                        )}
                    />
                </div>
                <div className='flex justify-end'>
                    <PhoneHQButton onClick={handleSubmit(onSubmit)}  phq-id={`integrationPp-saveSettingsBtn`}>Save changes</PhoneHQButton>

                </div>

            </div>

            <div className={'mx-auto max-w-[840px] pl-3'}>
                <div className=' border-b border-line pb-8'>
                    <div className='mb-3'>
                        <div className={'font-semibold mb-5 text-lg'}>
                            Data Import
                        </div>
                        <div
                            className='bg-brand-tint-subtle border border-brand flex gap-1 text-sm text-ink items-center mb-6'>
                            <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand-ink"><PhoneHQGlyph name='info' size={24} className='pl-4'/></span>
                            This process will import new records and update existing ones based on your
                            selected default country prefix.
                        </div>
                        <div className='flex justify-between'>
                            {integrationImport
                                ? <IntegrationStatusDisplay isIntegrationStarted={isIntegrationStarted}
                                                            integrationImport={integrationImport}/>
                                : !isIntegrationStarted && (
                                    <div className='text-sm text-ink-muted' phq-id={`integrationPp-notSyncedInfo`}>
                                        Not synced yet. Press Resync to import your Pipedrive contacts. Later changes sync automatically.
                                    </div>
                                )}
                            <PhoneHQButton size="sm" onClick={() => {
                                startPipedriveSync()
                            }} className='h-10' phq-id={`integrationPp-resyncBtn`}>Resync</PhoneHQButton>
                        </div>

                    </div>


                </div>
                <div className='flex justify-between items-center mt-5'>
                    <div className='font-semibold text-lg'>
                        Pipedrive account
                    </div>
                    <PhoneHQButton variant="text-danger" onClick={() => setDisconnectOpen(true)} className='my-4' phq-id={`integrationPp-uninstallBtn`}>Uninstall</PhoneHQButton>
                    <DisconnectIntegrationDialog open={disconnectOpen} onOpenChange={setDisconnectOpen} integrationId={Number(id)} platform={'PIPEDRIVE'}/>
                </div>
                <div className={'bg-surface-muted py-2  px-4 inline-block'}>
                    {settings?.details.companyName} {settings?.details.companyId}
                </div>
                <div className={'my-6 flex items-center '}>
                    <div className={'min-w-[500px]'}>
                        You can also read about integration in ou Knowledge Base Article
                    </div>
                    <div className={'w-full'}>
                        <a className={'flex items-center justify-between'} target='_blank'
                           href={'https://support.phonehq.com/en/collections/5-pipedrive'}><span
                            className='text-brand-ink underline'>Knowledge Base Article</span> <PhoneHQGlyph name='open_in_new' size={20}/> </a>
                    </div>

                </div>


            </div>
        </div>
    )
}
